import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getAutotagValue } from '~/grid/utils/styleGuideUtils';
import { desktop, useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import useGridInputs from '../../hooks/useGridInputs';
import useProductGridData from '../../hooks/useProductGridData';
import {
  isFacetOptionSelected,
  updateFiltersInUrlParams,
} from '../../utils/filters';
import { scrollToTopOfGrid } from '../../utils/scroll';

const DynamicFormattedMessage = FormattedMessage;

const FilterPill = styled.button`
  border: 1px solid black;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected
      ? theme.colors.palette.black
      : theme.colors.filterPillUnselected};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.palette.white : theme.colors.palette.black};
  border-radius: 9999px;
  transition: background-color 0.3s;
  cursor: pointer;
  font-family: inherit;
  text-transform: uppercase;
  border: none;
  width: max-content;
  ${({ theme }) => theme.gridStyles.discoveryGuideFilterPill};

  &:hover {
    ${desktop`
      background-color: black;
      color: white;
    `}
  }
`;

const DiscoveryGuideFilterPill = ({ options }) => {
  const { selectedUrlFilters } = useProductGridData();
  const { gridStyleGuide, collectionSelection } = useGridInputs();
  const router = useRouter();
  const { isMobile } = useBreakpoint();

  const facetName = `tag_${gridStyleGuide?.tagList}_list`;
  const facetOption = options?.customVars?.tag_name;

  const isSelected = isFacetOptionSelected({
    facetName,
    facetOption,
    selectedUrlFilters,
  });

  const handleClick = () => {
    updateFiltersInUrlParams({
      facetName,
      facetOption,
      router,
    });
    scrollToTopOfGrid({ isMobile });
  };

  // If facetOption is not defined, return null
  // This can happen if the CMS is not configured correctly for the tag_name asset field
  if (!facetOption) {
    return null;
  }

  // Replace spaces and dashes with underscores for the rbId
  const rbId = `${gridStyleGuide?.tagList}_${facetOption.replace(
    /[- ]/g,
    '_'
  )}`;

  const autotagValue = getAutotagValue({
    isPill: true,
    tagName: facetOption,
    activeCategoryLabel: collectionSelection?.value,
  });

  return (
    <FilterPill
      type="button"
      onClick={handleClick}
      $isSelected={isSelected}
      data-testid={`filter-pill-${gridStyleGuide?.tagList}`}
      data-autotag={autotagValue}
    >
      <DynamicFormattedMessage
        id={`site_product_filter.${rbId.toLowerCase()}`}
        defaultMessage={facetOption}
      />
    </FilterPill>
  );
};

DiscoveryGuideFilterPill.propTypes = {
  options: PropTypes.shape({
    customVars: PropTypes.shape({
      tag_name: PropTypes.string,
    }),
  }),
};

export default DiscoveryGuideFilterPill;
